// /clubs: mirrors screens/HomeScreen.js (feed, categories, search, Club Life, Apply flow).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useNavigationType } from "react-router-dom";
import { Band, Page } from "../components/Band";
import Icon from "../components/Icon";
import { Button, Chip, Content, Empty, FilterChip, SkeletonRows } from "../components/primitives";
import { ClubService } from "../../../services/clubService";
import useClubSearchTracking from "../../../hooks/useClubSearchTracking";
import useClubLifeTiles from "../../../components/home/useClubLifeTiles";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import useClubApply from "../hooks/useClubApply";
import useClubsFeed from "../components/clubs/useClubsFeed";
import useCategories from "../components/clubs/useCategories";
import ClubCard from "../components/clubs/ClubCard";
import ClubLifeCarousel from "../components/clubs/ClubLifeCarousel";
import CategorySheet from "../components/clubs/CategorySheet";
import ConfirmModal from "../components/manage/ConfirmModal";
import { categoryEmoji } from "../../../utils/categoryEmoji";
import { joinStateFor } from "../components/clubs/clubUtils";
import "../styles/clubs-web.css";

const PAGE_SIZE = 20;
// Search, category, loaded count and scroll survive opening a club and coming back (per tab session).
const LIST_KEY = "vl.web.clubs.list";
const RESTORE_MS = 4000; // the feed streams in pages; keep re-applying the saved scroll until it fits

function readListState() {
  try {
    const v = JSON.parse(sessionStorage.getItem(LIST_KEY) || "null");
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
}

export default function ClubsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userClubs, loadUserClubs } = useData();
  const { clubs, loading, error, reload } = useClubsFeed();
  const { categories } = useCategories();

  const navType = useNavigationType();
  const [saved] = useState(() => (navType === "POP" ? readListState() : {}));
  const [query, setQuery] = useState(typeof saved.query === "string" ? saved.query : "");
  // category object ({ id, name } when restored) or null = All
  const [category, setCategory] = useState(saved.category?.id ? saved.category : null);
  const [sheet, setSheet] = useState(false);
  const [shown, setShown] = useState(Number.isInteger(saved.shown) && saved.shown > PAGE_SIZE ? saved.shown : PAGE_SIZE);
  const [applications, setApplications] = useState([]);
  const [withdrawing, setWithdrawing] = useState(null); // club awaiting the withdraw confirm
  const searchRef = useRef(null);
  const rootRef = useRef(null);
  const scrollTop = useRef(0);
  const pendingScroll = useRef(saved.scroll > 0 ? { top: saved.scroll, until: Date.now() + RESTORE_MS } : null);
  const appsEpoch = useRef(0);

  // Pending applications (HomeScreen.js:371); re-read on mount and after every apply attempt.
  const fetchApplications = useCallback(async () => {
    if (!user) return;
    const epoch = ++appsEpoch.current;
    const { data, error: e } = await ClubService.getUserApplications(user.uid);
    if (epoch !== appsEpoch.current || e) return;
    setApplications(data || []);
  }, [user]);
  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const refresh = useCallback(() => { loadUserClubs(); fetchApplications(); }, [loadUserClubs, fetchApplications]);
  const { applyingClubId, applyBursts, onJoinPress } = useClubApply({ user, refresh });
  // Withdrawing removes the application, so it asks first (apply stays one tap).
  const onJoin = (club, state) => (state === "applied" ? setWithdrawing(club) : onJoinPress(club, state));

  // "/" focuses the search pill (unless typing somewhere already).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t?.tagName || "")) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const memberIds = useMemo(() => new Set((userClubs || []).map((c) => c.id)), [userClubs]);
  const pendingIds = useMemo(
    () => new Set(applications.filter((a) => a.status === "pending").map((a) => a.clubId)),
    [applications]
  );

  // Client-side search + category filter + member_count sort (HomeScreen.js:226-247).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...clubs];
    if (q) list = list.filter((c) => c.name?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
    if (category) list = list.filter((c) => c.category === category.name || c.category_id === category.id);
    return list.sort((a, b) => (b.member_count || 0) - (a.member_count || 0));
  }, [clubs, query, category]);

  useClubSearchTracking(query, filtered);
  // A new search or category starts from the first page (in the handlers, not an effect, so a restore keeps `shown`).
  const changeQuery = (q) => { setQuery(q); setShown(PAGE_SIZE); };
  const pickCategory = (c) => { setCategory(c); setShown(PAGE_SIZE); };

  // Track the page scroller (Page's .scroll) and save the list state whenever it changes and on leave.
  const persist = useCallback(() => {
    try {
      sessionStorage.setItem(LIST_KEY, JSON.stringify({
        query,
        category: category ? { id: category.id, name: category.name } : null,
        shown,
        scroll: scrollTop.current,
      }));
    } catch { /* storage blocked: the list just starts fresh */ }
  }, [query, category, shown]);
  const persistRef = useRef(persist);
  persistRef.current = persist;
  useEffect(() => { persist(); }, [persist]);
  useEffect(() => {
    const el = rootRef.current?.closest(".scroll");
    if (!el) return undefined;
    const onScroll = () => {
      const p = pendingScroll.current;
      if (p && Date.now() <= p.until) return; // our own restore scrolls
      pendingScroll.current = null;
      scrollTop.current = el.scrollTop;
    };
    const cancel = () => { pendingScroll.current = null; }; // the user took over
    const userEvents = ["wheel", "touchstart", "pointerdown", "keydown"];
    el.addEventListener("scroll", onScroll, { passive: true });
    userEvents.forEach((t) => el.addEventListener(t, cancel, { passive: true }));
    return () => {
      el.removeEventListener("scroll", onScroll);
      userEvents.forEach((t) => el.removeEventListener(t, cancel));
      persistRef.current();
    };
  }, []);

  const tiles = useClubLifeTiles(clubs);
  const open = (club) => navigate(`/clubs/${club.id}`, { state: { club, source: "home_list" } });
  const visible = filtered.slice(0, shown);
  const showLife = !query.trim() && !category && tiles.length > 0;

  // Re-apply the saved scroll as the streamed pages arrive, until it is reached or RESTORE_MS passes.
  useLayoutEffect(() => {
    const p = pendingScroll.current;
    const el = rootRef.current?.closest(".scroll");
    if (!p || !el || loading) return;
    if (Date.now() > p.until) { pendingScroll.current = null; return; }
    el.scrollTop = p.top;
    scrollTop.current = el.scrollTop;
    if (Math.abs(el.scrollTop - p.top) < 2) pendingScroll.current = null;
  }, [loading, visible.length, showLife]);

  const count = filtered.length;
  const emptyText = query.trim()
    ? "No clubs match that search."
    : category ? `No clubs in ${category.name} yet.` : "No clubs yet.";

  return (
    <Page>
      <Band title="Clubs" sub="Explore student organizations at UM" />
      <Content>
        <div className="search" ref={rootRef}>
          <Icon name="search" size={20} />
          <input
            ref={searchRef}
            placeholder="Search clubs"
            aria-label="Search clubs"
            data-testid="home.search"
            value={query}
            onChange={(e) => changeQuery(e.target.value)}
          />
          <span className="kbd hide-sm" aria-hidden="true">/</span>
          <Chip iconOnly icon="filter" style={{ boxShadow: "none", background: "var(--blue50)" }} aria-label="Filter by category" data-testid="home.filter" onClick={() => setSheet(true)} />
        </div>

        {showLife && (
          <>
            <div className="section-h"><h2>Club Life</h2><span className="meta">Swipe through campus</span></div>
            <ClubLifeCarousel tiles={tiles} onOpen={open} />
          </>
        )}

        <div className="section-h"><h2>Explore Clubs</h2><span className="meta">{loading ? "" : `${count} club${count === 1 ? "" : "s"}`}</span></div>
        <div className="chips" style={{ marginBottom: 14 }}>
          <FilterChip on={!category} data-testid="home.category.all" onClick={() => pickCategory(null)}>All</FilterChip>
          {categories.map((c) => (
            <FilterChip key={c.id} on={category?.id === c.id} data-testid={`home.category.${c.id}`} onClick={() => pickCategory(c)}>
              {categoryEmoji(c.name) ? `${categoryEmoji(c.name)} ` : ""}{c.name}
            </FilterChip>
          ))}
        </div>

        {loading ? (
          <SkeletonRows rows={6} />
        ) : error ? (
          <Empty icon="alert" title="Couldn't load clubs">
            <p className="meta" style={{ marginBottom: 12 }}>{error}</p>
            <Button onClick={reload} data-testid="home.retry">Retry</Button>
          </Empty>
        ) : (
          <>
            <div className="clubgrid">
              {visible.map((c) => {
                const state = joinStateFor(c, memberIds, pendingIds);
                return (
                  <ClubCard
                    key={c.id}
                    club={c}
                    state={state}
                    busy={applyingClubId === c.id}
                    burst={applyBursts[c.id] || 0}
                    onOpen={open}
                    onJoin={onJoin}
                  />
                );
              })}
            </div>
            {count === 0 && (
              <div className="empty card" data-testid="home.empty">
                {emptyText}
                {category && (
                  <div style={{ marginTop: 10 }}>
                    <Button size="sm" variant="sec" onClick={() => pickCategory(null)} data-testid="home.empty.showAll">Show all clubs</Button>
                  </div>
                )}
              </div>
            )}
            {filtered.length > shown && (
              <div className="row" style={{ justifyContent: "center", marginTop: 18 }}>
                <Button variant="sec" onClick={() => setShown((n) => n + PAGE_SIZE)} data-testid="home.loadMore">Load more</Button>
              </div>
            )}
          </>
        )}
      </Content>

      <button type="button" className="fab" data-testid="home.createClub" onClick={() => navigate("/clubs/new")}>
        <Icon name="plus" size={20} />Create Club
      </button>

      {withdrawing && (
        <ConfirmModal
          title="Withdraw application?"
          confirmLabel="Withdraw"
          danger
          testId="home.withdrawConfirm"
          onClose={() => setWithdrawing(null)}
          onConfirm={() => { const c = withdrawing; setWithdrawing(null); onJoinPress(c, "applied"); }}
        >
          Your application to {withdrawing.name} will be removed. You can apply again later.
        </ConfirmModal>
      )}

      {sheet && (
        <CategorySheet
          categories={categories}
          selected={category}
          onPick={(c) => { pickCategory(c); setSheet(false); }}
          onClose={() => setSheet(false)}
        />
      )}
    </Page>
  );
}
